import api from '~/utils/api';
import { computeCost, sumBy } from '~/utils/compute';
import { formatDate } from '~/utils/formatters';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { AsyncReturnType } from '~/utils/types';

type Order = AsyncReturnType<typeof api.fetchProtectionOrder>;

const getLatestClaim = (claims: Order['shippingClaims'] = []) => {
  const sorted = claims.sort((a, b) => b.createdOn.localeCompare(a.createdOn));

  return sorted[0];
};

export const { loader, useLoaderData: useProtectionOrderOverviewLoaderData } =
  makeLoader(({ params }, appStore) => {
    const { order } = appStore.getState();
    if (!order) {
      return redirect(`/${params.store ?? ''}`);
    }

    const latestClaim = getLatestClaim(order.shippingClaims);

    if (latestClaim) {
      return redirect(`claim/${latestClaim.id}/existing`);
    }

    const allowShippingClaimCreate =
      order.wasShippingProtected || order.isUnprotectedShippingClaimPermitted;

    return allowShippingClaimCreate ?
        {
          store: order.storeName,
          orderNo: order.orderNo,
          orderDate: formatDate(order.createdOn),
          price: sumBy(computeCost)(order.lineItems),
          postalCode: order.shippingAddress?.postalCode,
          wasShippingProtected: order.wasShippingProtected,
        }
      : redirect('not-protected');
  });

export const { action, useActionResult } = makeAction(
  ({ params, formData }, appStore) => {
    const { order } = appStore.getState();
    if (!order) {
      return redirect(`/${params.store ?? ''}`);
    }

    if (formData.get('action') !== 'startClaim') {
      return { ok: false, message: 'Invalid action' };
    }

    appStore.dispatch(
      appStore.actions.shippingProtectionClaim.startClaim({
        orderIdFromPlatform: order.idFromPlatform,
      }),
    );

    return redirect('new');
  },
);
